<template>
  <SettingsPagePanelHeader>
    <template #title>友だち追加経路別初期チャットメッセージ</template>
    <template #right>
      <FlatButton :class="$style.button" @click="handleCreate">
        <template #icon>
          <IconPlus :class="[$style.button__icon]" />
        </template>
        新規作成
      </FlatButton>
    </template>
  </SettingsPagePanelHeader>

  <div v-if="conditionalIntroductionMessages">
    <section v-if="!hasConditionalIntroductionMessage" :class="$style.content">
      登録がありません
    </section>
    <ul v-else :class="$style.list">
      <li
        v-for="message in conditionalIntroductionMessages"
        :key="message.condition.guid"
        :class="$style.list__item"
      >
        <RouterLink
          :to="introductionMessageSettingConditionalEdit(message.guid)"
          :class="$style['listItem']"
        >
          <p :class="[$style.keyword__text, $style.text]">
            {{ message.condition.inflowSourceName }}
          </p>
        </RouterLink>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import type { RouteLocationRaw } from 'vue-router'

import IconPlus from '@/components/icons/IconPlus.vue'
import FlatButton from '@/components/atoms/FlatButton.vue'
import SettingsPagePanelHeader from '../SettingsPagePanelHeader.vue'

type conditional = {
  guid: string
  condition: {
    guid: string
    inflowSourceName: string
  }
}

const props = defineProps<{
  conditionalIntroductionMessages: conditional[] | undefined
}>()

const route = useRoute('introductionMessageSetting')
const router = useRouter()

const handleCreate = async () => {
  // eslint-disable-next-line fp/no-mutating-methods
  await router.push({
    name: 'introductionMessageSettingConditionalCreate',
    params: {
      entranceGUID: route.params.entranceGUID,
    },
  })
}

const introductionMessageSettingConditionalEdit = (
  guid: string
): RouteLocationRaw => {
  return {
    name: 'introductionMessageSettingConditionalEdit',
    params: {
      entranceGUID: route.params.entranceGUID,
      conditionalGUID: guid,
    },
  }
}

const hasConditionalIntroductionMessage = computed(
  () =>
    props.conditionalIntroductionMessages !== undefined &&
    props.conditionalIntroductionMessages.length > 0
)
</script>

<style lang="scss" module>
.button {
  &:nth-child(n + 2) {
    margin-left: 16px;
  }
}

.content {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 21px;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
}

.list {
  display: flex;
  flex-direction: column;
  gap: 15px;

  &__item {
    &:hover {
      background-color: $color-surface-accent-primary-light;
    }
  }
}

.listItem {
  border: 1px solid $color-border-low-emphasis;
  border-radius: $border-radius-v2-base;
  display: flex;
  align-items: center;
  padding: 16px 16px 16px 43px;
  justify-content: space-between;
  gap: 16px;

  &:hover {
    text-decoration: none;
  }

  &__keyword {
    display: flex;
    gap: 16px;
  }
}

.text {
  color: $color-okaneko-blue;
  font-weight: bold;
}
</style>
